import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data?.session || null);
      setLoading(false);
    });

    // Only re-render on identity changes. Supabase-js also fires
    // `TOKEN_REFRESHED` (~hourly, on tab focus, on network reconnect)
    // and `USER_UPDATED`; those give us a fresh session **object** with
    // the same underlying user.id. If we called `setSession` on every
    // one of those, every consumer of `useAuth()` would receive a new
    // `user` object reference and any effect keyed on `user` (rather
    // than `user.id`) would re-fire, triggering a fetch → refresh → ...
    // loop that ends in a Supabase 429. So we compare the incoming
    // access_token + user.id and skip the state update when they match.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession((prev) => {
        const prevKey = prev
          ? `${prev.user?.id || ''}|${prev.access_token || ''}`
          : '';
        const nextKey = s ? `${s.user?.id || ''}|${s.access_token || ''}` : '';
        if (prevKey === nextKey) return prev; // identical → keep the ref stable
        return s;
      });
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    session,
    user: session?.user || null,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
